import type { GeoJSON } from "geojson";

import type { MapReviewLengthUnit } from "@/type/api";

/**
 * The draw modes' shape maths.
 *
 * Every draw-enabled tool hands the server raw GeoJSON — the Area filter
 * through `Spec.Area`, a zone through its version write — so a mode is only a
 * way of producing a closed ring: freehand samples a drag, circle approximates
 * a disc, and the clicked-vertex mode joins the points the operator clicks.
 * Keeping that production here, free of Vue, the DOM and every store, is what
 * makes it unit-testable in the node environment and callable from any surface
 * that commits a ring.
 *
 * The circle is sampled on the equirectangular sphere: metres east scale by
 * cos(latitude), which is exact enough at the radii a district draws (the error
 * over a kilometre is a fraction of a metre) and keeps the maths closed-form
 * and dependency-free. A circle must commit as a polygon because the whole
 * downstream path — the spec, the zone write, PostGIS — takes polygons and has
 * no circle primitive.
 */

/** A drawing mode as the operator picks it. */
export type DrawMode = "freehand" | "circle" | "polygon";

/** Every mode, in the order the selector offers them. */
export const DRAW_MODES: readonly DrawMode[] = [
	"freehand",
	"circle",
	"polygon",
];

/** The operator-facing name of each mode. */
export const DRAW_MODE_LABELS: Record<DrawMode, string> = {
	freehand: "Freehand",
	circle: "Circle",
	polygon: "Click vertices",
};

/** Mean Earth radius in metres (the IUGG mean), the sphere the circle's
 * equirectangular sampling is computed on. */
const EARTH_RADIUS_M = 6371008.8;

/** Metres in one degree of latitude on that sphere. A degree of longitude is
 * the same figure scaled by the cosine of the latitude. */
const METERS_PER_DEGREE = (EARTH_RADIUS_M * Math.PI) / 180;

/** Vertices a circle ring is sampled to when the caller names none: enough that
 * a circle the size of a district parcel reads as round at the zooms the
 * workbench draws at, and few enough that the ring stays a small payload. */
const DEFAULT_CIRCLE_STEPS = 64;

/**
 * A circle approximated as a closed polygon ring: `steps` vertices around
 * `center`, each `radiusMeters` metres away (equirectangular ground distance),
 * with the first vertex repeated to close the ring exactly as GeoJSON requires.
 */
export function circleRing(
	center: [number, number],
	radiusMeters: number,
	steps: number = DEFAULT_CIRCLE_STEPS,
): [number, number][] {
	const [lng, lat] = center;
	const metersPerDegLat = METERS_PER_DEGREE;
	const metersPerDegLng = METERS_PER_DEGREE * Math.cos((lat * Math.PI) / 180);
	const ring: [number, number][] = [];
	for (let i = 0; i < steps; i++) {
		const angle = (2 * Math.PI * i) / steps;
		ring.push([
			lng + (radiusMeters * Math.sin(angle)) / metersPerDegLng,
			lat + (radiusMeters * Math.cos(angle)) / metersPerDegLat,
		]);
	}
	// Repeat the first vertex: a GeoJSON ring's last point is its first point.
	ring.push([ring[0][0], ring[0][1]]);
	return ring;
}

/** The circle's ring as the closed polygon the `Spec.Area` / zone geometry path
 * expects. */
export function circlePolygon(
	center: [number, number],
	radiusMeters: number,
	steps?: number,
): GeoJSON.Polygon {
	return {
		type: "Polygon",
		coordinates: [circleRing(center, radiusMeters, steps)],
	};
}

/**
 * The closed polygon for a clicked-vertex ring, or null when the operator has
 * not clicked enough vertices for a ring at all. A path that already ends where
 * it began (a caller that closed it itself) is returned as it stands rather
 * than closed twice, which would leave a zero-length edge.
 */
export function vertexPolygon(
	vertices: [number, number][],
): GeoJSON.Polygon | null {
	if (vertices.length < 3) return null;
	const ring: [number, number][] = vertices.map((p) => [p[0], p[1]]);
	const first = ring[0];
	const last = ring[ring.length - 1];
	if (first[0] !== last[0] || first[1] !== last[1])
		ring.push([first[0], first[1]]);
	return { type: "Polygon", coordinates: [ring] };
}

/** Metres in one of the units a distance may be stated in, so a typed distance
 * becomes the metres every geometry call works in. */
const METERS_PER_UNIT: Record<MapReviewLengthUnit, number> = {
	m: 1,
	km: 1000,
	ft: 0.3048,
	mi: 1609.344,
};

/** A distance the operator typed, in metres. */
export function distanceToMeters(
	value: number,
	unit: MapReviewLengthUnit,
): number {
	return value * METERS_PER_UNIT[unit];
}
